import { useTu } from "@src/appV2/i18n/useTu";
import { ShiftInviteStatus } from "@clipboard-health/contract-backend-main";
import { formatShortDateTime, formatTime, isSameDay } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Button, Chip, type SxProps, type Theme } from "@mui/material";
import type { TuFunction } from "@src/appV2/i18n/types";
import { formatDurationBetweenDates } from "@src/appV2/lib/Time/formatDurationBetweenDates";
import { useDeleteShiftInvite } from "@src/appV2/ShiftInvites/api/useDeleteShiftInvite";
import { useGetShiftInvite } from "@src/appV2/ShiftInvites/api/useGetShiftInvite";
import { getEffectiveInviteStatus } from "@src/appV2/ShiftInvites/utils/getEffectiveInviteStatus";
import { PACIFIC_TIME_ZONE } from "@src/appV2/Timezone/constants";
import { ChatMessageCard } from "./ChatMessageCard";

interface ShiftInviteRequestMessageProps {
  shiftInviteId: string;
  sx?: SxProps<Theme>;
}

interface ShiftInviteChipProps {
  color: "success" | "error" | "default" | "warning";
  label: string;
  customStyles?: Record<string, string>;
}

const GRAY_CHIP_BACKGROUND_COLOR = "rgba(0, 0, 0, 0.08)";

function getShiftInviteStatusChipMap(
  tu: TuFunction
): Record<ShiftInviteStatus, ShiftInviteChipProps> {
  return {
    [ShiftInviteStatus.PENDING]: {
      color: "warning",
      label: tu("Pending"),
    },
    [ShiftInviteStatus.ACCEPTED]: {
      color: "success",
      label: tu("Accepted"),
    },
    [ShiftInviteStatus.DECLINED]: {
      color: "default",
      label: tu("Declined"),
      customStyles: {
        backgroundColor: GRAY_CHIP_BACKGROUND_COLOR,
      },
    },
    [ShiftInviteStatus.EXPIRED]: {
      color: "default",
      label: tu("Expired"),
      customStyles: {
        backgroundColor: GRAY_CHIP_BACKGROUND_COLOR,
      },
    },
    [ShiftInviteStatus.DELETED]: {
      color: "default",
      label: tu("Removed"),
      customStyles: {
        backgroundColor: GRAY_CHIP_BACKGROUND_COLOR,
      },
    },
    [ShiftInviteStatus.CANCELLED]: {
      color: "default",
      label: tu("Canceled"),
      customStyles: {
        backgroundColor: GRAY_CHIP_BACKGROUND_COLOR,
      },
    },
  };
}

/**
 * Format shift start and end time for display.
 */
function formatShiftStartEndDateTime(
  start: Date | string,
  end: Date | string,
  timeZone: string,
  tu: TuFunction
): string {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const formattedStart = formatShortDateTime(startDate, { timeZone });
  const formattedEnd = formatTime(endDate, { timeZone });

  const doesShiftEndOnSameDay = isSameDay(startDate, endDate, { timeZone });

  return doesShiftEndOnSameDay
    ? `${formattedStart} - ${formattedEnd}`
    : tu("{{formattedStart}} - {{formattedEnd}} (next day)", {
        values: { formattedStart, formattedEnd },
      });
}

/**
 * Renders a shift invite request message with rich card UI.
 * Shows shift details, status chip, expiration countdown, and remove action.
 */
export function ShiftInviteRequestMessage(props: ShiftInviteRequestMessageProps) {
  const { tu } = useTu("chat");

  const { shiftInviteId, sx } = props;

  const { data: shiftInviteResponse } = useGetShiftInvite(
    { shiftInviteId },
    { enabled: isDefined(shiftInviteId) }
  );

  const { mutateAsync: deleteShiftInviteMutate, isLoading: isDeletingShiftInvite } =
    useDeleteShiftInvite();

  const shiftInvite = shiftInviteResponse?.data;

  if (!isDefined(shiftInvite)) {
    return null;
  }

  const { status, expiresAt, shiftDetails, facility } = shiftInvite.attributes;

  const isExpired =
    getEffectiveInviteStatus(status, new Date(expiresAt)) === ShiftInviteStatus.EXPIRED;

  const displayDuration = formatDurationBetweenDates({
    fromDate: new Date(),
    toDate: new Date(expiresAt),
    padMinutes: false,
  });

  const timeZone = facility.tmz ?? PACIFIC_TIME_ZONE;

  const formattedShiftTime = formatShiftStartEndDateTime(
    shiftDetails.start,
    shiftDetails.end,
    timeZone,
    tu
  );

  const isActionable = status === ShiftInviteStatus.PENDING && !isExpired;

  const statusChipMap = getShiftInviteStatusChipMap(tu);
  const chipProps = isExpired ? statusChipMap[ShiftInviteStatus.EXPIRED] : statusChipMap[status];

  const handleRemove = async () => {
    await deleteShiftInviteMutate(shiftInviteId);
  };

  const statusChip = (
    <Chip
      aria-label={chipProps.label}
      color={chipProps.color}
      label={chipProps.label}
      size="small"
      sx={{
        fontWeight: "bold",
        ...chipProps.customStyles,
      }}
      variant="filled"
    />
  );

  const removeButton = isActionable ? (
    <Button
      color="error"
      disabled={isDeletingShiftInvite}
      size="small"
      variant="outlined"
      onClick={handleRemove}
    >
      {tu("Remove")}
    </Button>
  ) : undefined;

  return (
    <ChatMessageCard
      actions={removeButton}
      caption={
        isActionable
          ? tu("Expiring in {{displayDuration}}", { values: { displayDuration } })
          : undefined
      }
      statusChip={statusChip}
      subtitle={formattedShiftTime}
      sx={sx}
      title={tu("You sent a shift invite")}
    />
  );
}
